/**
 * src/pages/Welcome.js
 * One-time page to display API keys after account creation.
 * Keys are stored in sessionStorage and cleared on navigation to dashboard.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function Welcome() {
  const [keys, setKeys] = useState(null);
  const [copiedWrite, setCopiedWrite] = useState(false);
  const [copiedRead, setCopiedRead] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('pt_signup_keys');
      if (stored) {
        setKeys(JSON.parse(stored));
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  async function copyToClipboard(text, setter) {
    try {
      await navigator.clipboard.writeText(text);
      setter(true);
      setTimeout(() => setter(false), 2000);
    } catch {
      // Fallback: select text for manual copy
    }
  }

  function handleContinue() {
    sessionStorage.removeItem('pt_signup_keys');
    if (user?.emailVerified === false) {
      navigate('/verify-email-pending');
    } else {
      navigate('/dashboard');
    }
  }

  if (!keys) return null;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--content-bg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px'
    }}>
      <div style={{ width: '100%', maxWidth: '520px' }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '32px' }}>
          <div style={{
            width: '36px', height: '36px',
            background: 'var(--accent)',
            borderRadius: '8px'
          }} />
          <div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Process Trails
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              Client Portal
            </div>
          </div>
        </div>

        {/* Card */}
        <div className="card">
          <div className="card-body">
            <h1 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>
              Your API keys
            </h1>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '24px' }}>
              Your account has been created. Save the keys below — they will only be shown once.
            </p>

            {/* Warning */}
            <div style={{
              background: '#fef3c7',
              border: '1px solid #f59e0b',
              borderRadius: '6px',
              padding: '12px 14px',
              marginBottom: '24px',
              fontSize: '13px',
              color: '#92400e',
              lineHeight: '1.5'
            }}>
              These keys cannot be retrieved after you leave this page.
              Store them in a safe place before continuing.
            </div>

            {/* Write key */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '500',
                color: 'var(--text-primary)',
                marginBottom: '6px'
              }}>
                Write API key
                <span style={{
                  fontSize: '11px',
                  color: 'var(--text-secondary)',
                  fontWeight: '400',
                  marginLeft: '8px'
                }}>
                  Used by the Chrome extension to send events
                </span>
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  className="form-input"
                  type="text"
                  value={keys.write}
                  readOnly
                  style={{
                    flex: 1,
                    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
                    fontSize: '12px',
                    background: 'var(--sidebar-bg)',
                    userSelect: 'all'
                  }}
                  onClick={e => e.target.select()}
                />
                <button
                  className="btn btn-secondary"
                  onClick={() => copyToClipboard(keys.write, setCopiedWrite)}
                  style={{ whiteSpace: 'nowrap', minWidth: '70px' }}
                >
                  {copiedWrite ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            {/* Read key */}
            <div style={{ marginBottom: '28px' }}>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '500',
                color: 'var(--text-primary)',
                marginBottom: '6px'
              }}>
                Read API key
                <span style={{
                  fontSize: '11px',
                  color: 'var(--text-secondary)',
                  fontWeight: '400',
                  marginLeft: '8px'
                }}>
                  Used to query event data via the Read API
                </span>
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  className="form-input"
                  type="text"
                  value={keys.read}
                  readOnly
                  style={{
                    flex: 1,
                    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
                    fontSize: '12px',
                    background: 'var(--sidebar-bg)',
                    userSelect: 'all'
                  }}
                  onClick={e => e.target.select()}
                />
                <button
                  className="btn btn-secondary"
                  onClick={() => copyToClipboard(keys.read, setCopiedRead)}
                  style={{ whiteSpace: 'nowrap', minWidth: '70px' }}
                >
                  {copiedRead ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            <button
              className="btn btn-primary w-full"
              onClick={handleContinue}
              style={{ justifyContent: 'center', padding: '10px' }}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
